import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, ArrowLeft, Plus, ShoppingBag, GitFork, Network, Users } from "lucide-react";
import LineageSubmitModal from "../components/LineageSubmitModal";
import ClassificationChip from "../components/ClassificationChip";
import GrowLogStrip from "../components/GrowLogStrip";
import StrainProofStats from "../components/StrainProofStats";
import ForgeIndexPanel from "../components/ForgeIndexPanel";
import VaultBadge from "../components/VaultBadge";
import ShareButton from "../components/ShareButton";
import StrainTrendingStrip from "../components/StrainTrendingStrip";
import ParentageTree, { isInvalidParentText } from "../components/lineage/ParentageTree";
import { LINEAGE_STATUS } from "./LineageAtlas";
import {
  GENETIC_ENTRY_TYPES, PARENT_ROLES, PARENT_TYPES, GENERATIONS,
  SELECTION_DIRECTIONS, POPULATION_TYPES, PRESERVATION_STATUSES,
  ENVIRONMENT_ADAPTATIONS, CUT_AUTHENTICITY, YES_NO_UNKNOWN, LINEAGE_CONFIDENCE,
  EVIDENCE_TYPES, DISPUTE_STATUSES, GENETIC_MARKER_TYPES, CHEMOTYPES,
  LEAF_TYPES, PHOTOPERIOD_TYPES, SEX_EXPRESSIONS, RESIN_PRODUCTION,
  EVIDENCE_FILE_CATEGORIES,
} from "../lib/lineageFormOptions";
import { listingUrl } from "../lib/urls";

// Lookup table — translates the stored canonical value codes (e.g. "modern_hybrid",
// "type_i") into human-readable labels for the public detail page.
const buildLookup = (opts) => Object.fromEntries(opts);
const DEEP_LABELS = {
  entry: buildLookup(GENETIC_ENTRY_TYPES),
  role: buildLookup(PARENT_ROLES),
  ptype: buildLookup(PARENT_TYPES),
  gen: buildLookup(GENERATIONS),
  sel: buildLookup(SELECTION_DIRECTIONS),
  pop: buildLookup(POPULATION_TYPES),
  preserve: buildLookup(PRESERVATION_STATUSES),
  env: buildLookup(ENVIRONMENT_ADAPTATIONS),
  cut: buildLookup(CUT_AUTHENTICITY),
  yn: buildLookup(YES_NO_UNKNOWN),
  conf: buildLookup(LINEAGE_CONFIDENCE),
  evid: buildLookup(EVIDENCE_TYPES),
  disp: buildLookup(DISPUTE_STATUSES),
  marker: buildLookup(GENETIC_MARKER_TYPES),
  chemo: buildLookup(CHEMOTYPES),
  leaf: buildLookup(LEAF_TYPES),
  photo: buildLookup(PHOTOPERIOD_TYPES),
  sex: buildLookup(SEX_EXPRESSIONS),
  resin: buildLookup(RESIN_PRODUCTION),
  efile: buildLookup(EVIDENCE_FILE_CATEGORIES),
};

// Tiny presentational helper — renders a label + value pair styled for the
// lineage detail sections. Empty/missing values render as an em-dash so the
// card still feels balanced.
function DeepStat({ label, value }) {
  const show = value !== undefined && value !== null && value !== "" ? value : "—";
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-0.5"
           style={{color:"var(--text-muted)"}}>{label}</div>
      <div className="text-xs" style={{color:"var(--text)"}}>{show}</div>
    </div>
  );
}

export default function LineageDetail() {
  const { id_or_slug } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);

  // v3.99br — Canonical strain URL prefers /strains/:slug. /lineage/:slug
  // still resolves but emits the same canonical so search engines collapse
  // duplicate paths into a single ranked entity. Rich JSON-LD describes
  // the strain as a Product with Brand (breeder) + image set, and as a
  // Thing with isVariantOf/parent relationships when lineage is known.
  const canonicalPath = doc ? `/strains/${doc.slug || doc.id}` : "/lineage";
  const breederName = doc?.originator_text || (doc?.originator_breeder_name) || null;
  const heroImage = doc?.images?.[doc?.featured_image_index ?? 0] || doc?.images?.[0] || null;
  const parents = doc ? [doc.parent_one, doc.parent_two].filter((p) => p && !isInvalidParentText(p)) : [];
  const childrenList = (doc?.children || []).slice(0, 12);

  // Compose JSON-LD: graph of Breadcrumb + Product (strain as collectable
  // product) + auxiliary Thing for lineage relationships when available.
  const jsonLd = doc ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thecannaforge.com/" },
          { "@type": "ListItem", "position": 2, "name": "Strains", "item": "https://thecannaforge.com/lineage" },
          { "@type": "ListItem", "position": 3, "name": doc.strain_name,
            "item": `https://thecannaforge.com/strains/${doc.slug || doc.id}` },
        ],
      },
      {
        "@type": "Product",
        "@id": `https://thecannaforge.com/strains/${doc.slug || doc.id}#strain`,
        "name": doc.strain_name,
        "alternateName": doc.aliases || [],
        "description": (doc.lineage_notes || (parents.length === 2
          ? `${doc.strain_name} — ${parents[0]} × ${parents[1]}. Lineage record on CannaForge.`
          : `${doc.strain_name} — cannabis cultivar record on CannaForge Lineage Atlas.`)).slice(0, 1000),
        "image": heroImage ? [heroImage] : [],
        "category": doc.category || doc.strain_family || "Cannabis cultivar",
        ...(breederName ? { "brand": { "@type": "Brand", "name": breederName } } : {}),
        "additionalType": "https://thecannaforge.com/schema/CannabisCultivar",
        "additionalProperty": [
          ...(doc.seed_type ? [{ "@type": "PropertyValue", "name": "Seed type", "value": doc.seed_type }] : []),
          ...(doc.cycle_type ? [{ "@type": "PropertyValue", "name": "Cycle type", "value": doc.cycle_type }] : []),
          ...(doc.generation ? [{ "@type": "PropertyValue", "name": "Generation", "value": doc.generation }] : []),
          ...(doc.strain_family ? [{ "@type": "PropertyValue", "name": "Family", "value": doc.strain_family }] : []),
          ...(doc.landrace ? [{ "@type": "PropertyValue", "name": "Landrace", "value": "true" }] : []),
          ...(doc.heirloom ? [{ "@type": "PropertyValue", "name": "Heirloom", "value": "true" }] : []),
          ...(doc.preservation ? [{ "@type": "PropertyValue", "name": "Preservation", "value": "true" }] : []),
        ],
        ...(parents.length > 0 ? {
          "isBasedOn": parents.map((p) => ({ "@type": "Product", "name": p })),
        } : {}),
        ...(childrenList.length > 0 ? {
          "hasPart": childrenList.map((c) => ({ "@type": "Product", "name": c.strain_name, "url": `https://thecannaforge.com/strains/${c.slug || c.id}` })),
        } : {}),
      },
    ],
  } : null;

  useSeo({
    title: doc
      ? `${doc.strain_name}${parents.length === 2 ? ` (${parents.join(" × ")})` : ""} — Strain & Lineage · CannaForge`
      : "Strain — CannaForge",
    description: doc
      ? (
          (isInvalidParentText(doc.parent_one) && isInvalidParentText(doc.parent_two))
            ? `${doc.strain_name} cannabis strain record · foundation cultivar with no recorded parentage. ${doc.verification_status} on CannaForge — verified breeder ecosystem and genetics archive.`
            : `${doc.strain_name} cannabis strain record · ${doc.parent_one || "?"} × ${doc.parent_two || "?"}. ${doc.verification_status} on CannaForge — lineage, family, breeder attribution, related crosses, preservation status.`
        )
      : "",
    canonical: canonicalPath,
    ogTitle: doc ? `${doc.strain_name} — Lineage & Strain Record` : undefined,
    ogImage: heroImage || undefined,
    ogType: "product",
    // v3.99c.15 — Honour admin-set seo_noindex. Excluded from sitemap AND
    // emits <meta name="robots" content="noindex"> on the live page.
    noindex: !!(doc && doc.seo_noindex),
    jsonLd,
  });

  const load = () => {
    setErr(""); setDoc(null);
    api.get(`/strains/${id_or_slug}`).then(r => setDoc(r.data)).catch(e => setErr(formatApiError(e)));
  };
  useEffect(load, [id_or_slug]);

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save strains"); return; }
    setSaveBusy(true);
    try {
      const r = await api.post(`/strains/${doc.id}/save`);
      setDoc((d) => d ? { ...d, saved_by_me: r.data.saved, save_count: r.data.save_count } : d);
      toast.success(r.data.saved ? "Saved to your collection" : "Removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaveBusy(false); }
  };

  if (err) return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center text-sm" style={{color:"var(--text-2)"}}>{err}</div>;
  if (!doc) return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center text-sm" style={{color:"var(--text-muted)"}}>Loading…</div>;

  const statusMeta = LINEAGE_STATUS[doc.verification_status] || LINEAGE_STATUS.pending;

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="lineage-detail-page">
      <Link to="/lineage" className="inline-flex items-center gap-1.5 text-xs mb-5 underline"
            style={{color:"var(--text-muted)"}}>
        <ArrowLeft className="w-3 h-3"/> Back to atlas
      </Link>

      <header className="mb-6 sm:mb-8 flex items-start gap-4 sm:gap-5 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Lineage record</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-tighter leading-none break-words" style={{color:"var(--text)"}}>
            {doc.strain_name}
          </h1>
          {(doc.aliases || []).length > 0 && (
            <div className="mt-2 text-sm font-mono" style={{color:"var(--text-muted)"}}>
              aka {doc.aliases.join(" · ")}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-1 font-mono text-[11px] uppercase tracking-[0.16em] border"
                  style={{color: statusMeta.color, borderColor: statusMeta.color}}
                  data-testid="lineage-status-badge">
              <statusMeta.Icon className="w-3 h-3"/> {statusMeta.label}
            </span>
            {/* v3.99ae — Compact proof-stats next to status badge. Renders
                "No grow reports yet" on the detail page so the trust cue
                is always visible (versus the card variant which hides). */}
            <StrainProofStats strainIdOrSlug={doc.id} showEmpty size="detail"
                              testId="lineage-detail-proof-stats" />
            <VaultBadge strainId={doc.id} />
            {doc.landrace && (
              <span className="inline-flex items-center px-2 py-1 font-mono text-[11px] uppercase tracking-[0.16em] border"
                    style={{color:"var(--gold)", borderColor:"var(--gold)"}}>landrace</span>
            )}
            {doc.heirloom && (
              <span className="inline-flex items-center px-2 py-1 font-mono text-[11px] uppercase tracking-[0.16em] border"
                    style={{color:"var(--gold)", borderColor:"var(--gold)"}}>heirloom</span>
            )}
            {doc.preservation && (
              <span className="inline-flex items-center px-2 py-1 font-mono text-[11px] uppercase tracking-[0.16em] border"
                    style={{color:"var(--primary-text)", borderColor:"var(--primary-text)"}}>preservation</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={toggleSave} disabled={saveBusy}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 border text-sm font-mono uppercase tracking-[0.16em] ${doc.saved_by_me ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--border)] text-[var(--text-2)]"} hover:border-[var(--gold)] transition-colors`}
                  data-testid="lineage-save-btn">
            {doc.saved_by_me ? <BookmarkCheck className="w-4 h-4"/> : <Bookmark className="w-4 h-4"/>}
            {doc.saved_by_me ? "Saved" : "Save strain"}
            {(doc.save_count || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-[var(--surface-2)] text-[10px]">{doc.save_count}</span>
            )}
          </button>
          {/* v3.99c.69 — Share */}
          <ShareButton
            title={`${doc.strain_name} — CannaForge`}
            description={doc.lineage_notes || `${doc.strain_name} lineage on CannaForge.`}
            path={`/strains/${doc.slug || doc.id}`}
            testid="lineage-detail-share"
          />
          {user && (
            <button onClick={() => setCorrectOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-[var(--border)] hover:border-[var(--gold)] text-xs font-mono uppercase tracking-[0.16em]"
                    style={{color:"var(--text-2)"}}
                    data-testid="lineage-correct-btn">
              <Plus className="w-3 h-3"/> Suggest correction
            </button>
          )}
        </div>
      </header>

      {/* v3.70 — image gallery (admin/approved breeder/collector submissions). */}
      {(doc.images || []).length > 0 && (
        <LineageGallery images={doc.images} featuredIndex={doc.featured_image_index || 0}
                        strainName={doc.strain_name}/>
      )}

      {/* v3.97 — Magazine layout. Body lives in a 12-col grid with a sticky
          right rail offering quick-jump anchors, breeder credit, and the
          primary save / buy CTAs. Mirrors the design_guidelines.json
          "detail_page" spec. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        <main className="lg:col-span-8 min-w-0 space-y-6">

      {/* Parentage tree — multi-generation visual graph */}
      <ParentageTree doc={doc}/>

      {/* Meta grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Meta label="Classification" value={doc.classification ? (
          <ClassificationChip value={doc.classification} size="md"
                              testId={`lineage-meta-classification`}/>
        ) : "—"}/>
        <Meta label="Family" value={
          doc.strain_family ? (
            <Link to={`/families/${doc.strain_family.toLowerCase()}`}
                  className="underline-offset-2 hover:underline"
                  style={{ color: "var(--gold)" }}
                  data-testid="lineage-meta-family-link">
              {doc.strain_family}
            </Link>
          ) : "—"
        }/>
        <Meta label="Category" value={doc.category || "—"}/>
        <Meta label="Seed type" value={doc.seed_type || "—"}/>
      </section>

      {/* v3.99c.40 — Trait tag links: each chip routes back to a real SEO
          landing page (/classifications, /terpenes, /aromas, /structures,
          /preservation), strengthening the internal entity graph. */}
      {(doc.classification || (doc.terpene_tags || []).length > 0 ||
        (doc.aroma_tags || []).length > 0 || doc.plant_structure ||
        doc.landrace || doc.heirloom || doc.preservation || doc.seed_type) && (
        <section className="mb-8" data-testid="lineage-trait-links">
          <div className="label-eyebrow mb-2" style={{ color: "var(--text-muted)" }}>
            Trait pages
          </div>
          <div className="flex flex-wrap gap-1.5">
            {doc.classification && (
              <Link to={`/classifications/${doc.classification.toLowerCase().replace(/_/g, "-")}`}
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="lineage-trait-classification">
                {doc.classification}
              </Link>
            )}
            {(doc.terpene_tags || []).map((t) => (
              <Link key={`tp-${t}`} to={`/terpenes/${t}`}
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid={`lineage-trait-terpene-${t}`}>
                {t}
              </Link>
            ))}
            {(doc.aroma_tags || []).map((a) => (
              <Link key={`ar-${a}`} to={`/aromas/${a}`}
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid={`lineage-trait-aroma-${a}`}>
                {a.replace(/-/g, " ")}
              </Link>
            ))}
            {doc.plant_structure && (
              <Link to={`/structures/${doc.plant_structure}`}
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="lineage-trait-structure">
                {doc.plant_structure.replace(/-/g, " ")}
              </Link>
            )}
            {doc.landrace && (
              <Link to="/preservation/landrace"
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border px-2 py-1"
                    style={{ color: "var(--gold)", borderColor: "var(--gold)", borderRadius: 2 }}
                    data-testid="lineage-trait-landrace">
                landrace
              </Link>
            )}
            {doc.heirloom && (
              <Link to="/preservation/heirloom"
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="lineage-trait-heirloom">
                heirloom
              </Link>
            )}
            {doc.seed_type && (
              <Link to={`/seeds/${doc.seed_type.replace(/_/g, "-")}`}
                    className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1"
                    style={{ color: "var(--text-2)", borderRadius: 2 }}
                    data-testid="lineage-trait-seed-type">
                {doc.seed_type.replace(/_/g, " ")}
              </Link>
            )}
          </div>
        </section>
      )}

      {(doc.related_breeders || []).length > 0 && (
        <section id="related-breeders" className="scroll-mt-8">
          <h2 className="font-display text-2xl mb-3" style={{color:"var(--text)"}}>Related breeders</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {doc.related_breeders.map(v => (
              <Link key={v.id} to={`/breeder/${v.store_slug || v.id}`}
                    className="block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] p-3"
                    data-testid={`lineage-related-breeder-${v.id}`}>
                {v.logo_url && <img src={v.logo_url} alt="" className="w-12 h-12 object-cover border border-[var(--border)] mb-2"/>}
                <div className="font-serif text-sm" style={{color:"var(--text)"}}>{v.store_name}</div>
                <div className="text-[11px] font-mono flex items-center gap-1.5 flex-wrap" style={{color:"var(--text-muted)"}}>
                  <span>★ {(v.rating || 0).toFixed(1)} · {v.review_count || 0} reviews</span>
                  <span className="inline-flex items-center gap-0.5"
                        data-testid={`lineage-related-breeder-followers-${v.id}`}>
                    <Users className="w-2.5 h-2.5" />
                    {v.follower_count || 0}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(doc.related_listings || []).length > 0 ? (
        <section id="available-on-cannaforge" className="scroll-mt-8" data-testid="lineage-available-on-cannaforge">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="font-display text-2xl" style={{color:"var(--text)"}}>
              Available on CannaForge
            </h2>
            <span className="text-xs font-mono" style={{color:"var(--primary-text)"}}>
              {doc.related_listings.length} live listing{doc.related_listings.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {doc.related_listings.map(l => (
              <Link key={l.id} to={listingUrl(l)}
                    className="block bg-[var(--surface)] border border-[var(--primary-text)]/40 hover:border-[var(--primary-text)] p-3 transition-colors"
                    style={{borderLeftWidth: 3}}
                    data-testid={`lineage-related-listing-${l.id}`}>
                {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full aspect-square object-cover mb-2"/>}
                <div className="font-serif text-sm truncate" style={{color:"var(--text)"}}>{l.title}</div>
                <div className="text-[11px] font-mono truncate" style={{color:"var(--text-muted)"}}>{l.store_name}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="font-mono text-sm" style={{color:"var(--primary-text)"}}>${(l.price || 0).toFixed(2)}</div>
                  <ShoppingBag className="w-3 h-3" style={{color:"var(--primary-text)"}}/>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section id="available-on-cannaforge" className="scroll-mt-8 bg-[var(--surface)] border border-dashed border-[var(--border)] p-5 text-center text-sm"
                 style={{color:"var(--text-muted)"}}
                 data-testid="lineage-no-listings">
          No active CannaForge listings for <strong style={{color:"var(--text-2)"}}>{doc.strain_name}</strong> yet.
          {user ? (
            <> Hit <strong style={{color:"var(--gold)"}}>Save strain</strong> above and we'll notify you the moment a breeder lists it.</>
          ) : (
            <> <Link to="/login" className="underline" style={{color:"var(--gold)"}}>Sign in</Link> and save this strain to get a notification when a breeder lists it.</>
          )}
        </section>
      )}

      {/* Children / descendants section */}
      {(doc.children || []).length > 0 && (
        <section id="descendants" className="scroll-mt-8" data-testid="lineage-children">
          <div className="flex items-baseline gap-2 mb-3">
            <GitFork className="w-4 h-4" style={{color:"var(--gold)"}}/>
            <h2 className="font-display text-2xl" style={{color:"var(--text)"}}>Descendants</h2>
            <span className="text-xs font-mono" style={{color:"var(--text-muted)"}}>
              {doc.children.length} strain{doc.children.length === 1 ? "" : "s"} bred from {doc.strain_name}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {doc.children.map(c => (
              <Link key={c.id} to={`/strains/${c.slug || c.id}`}
                    className="block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] p-3"
                    data-testid={`lineage-child-${c.id}`}>
                <div className="font-serif text-sm" style={{color:"var(--text)"}}>{c.strain_name}</div>
                <div className="text-[11px] font-mono mt-1" style={{color:"var(--text-muted)"}}>
                  {c.parent_one || "?"} × {c.parent_two || "?"}
                </div>
                <div className="flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-[0.12em]">
                  <span style={{color: c.verification_status === "verified" ? "var(--primary-text)" : "var(--text-muted)"}}>
                    {c.verification_status}
                  </span>
                  {c.has_listings && (
                    <span className="ml-auto inline-flex items-center gap-1" style={{color:"var(--primary-text)"}}>
                      <ShoppingBag className="w-2.5 h-2.5"/> live
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* v3.99c.15 — Siblings (shared parentage). Strong internal-linking
          surface for both readers and crawlers. */}
      {(doc.siblings || []).length > 0 && (
        <section id="siblings" className="scroll-mt-8 mb-8" data-testid="lineage-siblings">
          <div className="flex items-baseline gap-2 mb-3">
            <GitFork className="w-4 h-4" style={{color:"var(--gold)"}}/>
            <h2 className="font-display text-2xl" style={{color:"var(--text)"}}>Siblings</h2>
            <span className="text-xs font-mono" style={{color:"var(--text-muted)"}}>
              Share at least one parent with {doc.strain_name}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {doc.siblings.map(s => (
              <Link key={s.id} to={`/strains/${s.slug || s.id}`}
                    className="block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] p-3"
                    data-testid={`lineage-sibling-${s.id}`}>
                <div className="font-serif text-sm" style={{color:"var(--text)"}}>{s.strain_name}</div>
                <div className="text-[11px] font-mono mt-1" style={{color:"var(--text-muted)"}}>
                  {s.parent_one || "?"} × {s.parent_two || "?"}
                </div>
                {s.has_listings && (
                  <div className="flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-[0.12em]" style={{color:"var(--primary-text)"}}>
                    <ShoppingBag className="w-2.5 h-2.5"/> live listings
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* v3.99c.15 — Family peers. Surfaces top strains from the same
          family for deeper topical clustering. */}
      {(doc.family_peers || []).length > 0 && (
        <section id="family-peers" className="scroll-mt-8 mb-8" data-testid="lineage-family-peers">
          <div className="flex items-baseline gap-2 mb-3">
            <GitFork className="w-4 h-4" style={{color:"var(--gold)"}}/>
            <h2 className="font-display text-2xl" style={{color:"var(--text)"}}>
              More from the {doc.strain_family} family
            </h2>
            {doc.strain_family && (
              <Link to={`/families/${doc.strain_family.toLowerCase()}`}
                    className="text-[11px] font-mono uppercase tracking-[0.2em] underline ml-2"
                    style={{color:"var(--primary-text)"}}>
                See all →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {doc.family_peers.map(p => (
              <Link key={p.id} to={`/strains/${p.slug || p.id}`}
                    className="block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] p-3"
                    data-testid={`lineage-family-peer-${p.id}`}>
                <div className="font-serif text-sm" style={{color:"var(--text)"}}>{p.strain_name}</div>
                {p.has_listings && (
                  <div className="flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-[0.12em]" style={{color:"var(--primary-text)"}}>
                    <ShoppingBag className="w-2.5 h-2.5"/> live
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {(doc.tags || []).length > 0 && (
        <section className="mb-8">
          <div className="label-eyebrow mb-2" style={{color:"var(--text-muted)"}}>Tags</div>
          <div className="flex flex-wrap gap-2">
            {doc.tags.map((t, i) => (
              <span key={i} className="px-2 py-1 text-xs font-mono border border-[var(--border)]"
                    style={{color:"var(--text-2)"}}>#{t}</span>
            ))}
          </div>
        </section>
      )}

      {/* ─── Deep lineage data (v3.74) — only render the sections the record
          actually has data for. Legacy records pass through untouched. ─── */}
      {(doc.genetic_entry_type || doc.generation || doc.selection_direction || doc.plants_selected_from !== undefined) && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-classification">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Classification & lineage profile</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <DeepStat label="Entry type" value={DEEP_LABELS.entry[doc.genetic_entry_type]}/>
            <DeepStat label="Generation" value={DEEP_LABELS.gen[doc.generation]}/>
            <DeepStat label="Selection" value={DEEP_LABELS.sel[doc.selection_direction]}/>
            <DeepStat label="Plants selected from" value={doc.plants_selected_from}/>
          </div>
        </section>
      )}

      {(doc.parent_one_detail || doc.parent_two_detail) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="lineage-deep-parents">
          {[doc.parent_one_detail, doc.parent_two_detail].filter(Boolean).map((p, i) => (
            <div key={i} className="bg-[var(--surface-2)] border border-[var(--border)] p-4">
              <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Parent {i + 1}</div>
              <div className="font-serif text-lg mb-1" style={{color:"var(--text)"}}>{p.name || "—"}</div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono mt-3" style={{color:"var(--text-muted)"}}>
                {p.role && <div>Role: <span style={{color:"var(--text-2)"}}>{DEEP_LABELS.role[p.role] || p.role}</span></div>}
                {p.type && <div>Type: <span style={{color:"var(--text-2)"}}>{DEEP_LABELS.ptype[p.type] || p.type}</span></div>}
                {p.source_breeder && <div className="col-span-2">Source: <span style={{color:"var(--text-2)"}}>{p.source_breeder}</span></div>}
              </div>
            </div>
          ))}
        </section>
      )}

      {doc.landrace_origin && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-origin">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Landrace / origin</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <DeepStat label="Region" value={doc.landrace_origin.region}/>
            <DeepStat label="Locality" value={doc.landrace_origin.locality}/>
            <DeepStat label="Collection year" value={doc.landrace_origin.collection_year}/>
            <DeepStat label="Collector" value={doc.landrace_origin.collector}/>
            <DeepStat label="Accession" value={doc.landrace_origin.accession_id}/>
            <DeepStat label="Population type" value={DEEP_LABELS.pop[doc.landrace_origin.population_type]}/>
            <DeepStat label="Preservation" value={DEEP_LABELS.preserve[doc.landrace_origin.preservation_status]}/>
            <DeepStat label="Pop. size" value={doc.landrace_origin.open_pollinated_population_size}/>
          </div>
          {(doc.landrace_origin.environmental_adaptation || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {doc.landrace_origin.environmental_adaptation.map(a => (
                <span key={a} className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] border border-[var(--border)]" style={{color:"var(--text-2)"}}>
                  {DEEP_LABELS.env[a] || a}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {doc.clone_details && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-clone">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Clone-only details</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Cut name" value={doc.clone_details.cut_name}/>
            <DeepStat label="Original holder" value={doc.clone_details.original_holder}/>
            <DeepStat label="Suspected lineage" value={doc.clone_details.suspected_lineage}/>
            <DeepStat label="Authenticity" value={DEEP_LABELS.cut[doc.clone_details.cut_authenticity_status]}/>
            <DeepStat label="Reversal used" value={DEEP_LABELS.yn[doc.clone_details.reversal_used]}/>
            <DeepStat label="Selfed line" value={DEEP_LABELS.yn[doc.clone_details.selfed_line_exists]}/>
          </div>
          {doc.clone_details.reversal_notes && (
            <p className="text-xs mt-3 whitespace-pre-line" style={{color:"var(--text-2)"}}>{doc.clone_details.reversal_notes}</p>
          )}
        </section>
      )}

      {doc.chemotype && (
        <section id="cultivar-data" className="scroll-mt-8 bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-chemotype">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Chemotype & terpenes</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Chemotype" value={DEEP_LABELS.chemo[doc.chemotype.chemotype]}/>
            <DeepStat label="THC range" value={doc.chemotype.thc_range}/>
            <DeepStat label="CBD range" value={doc.chemotype.cbd_range}/>
            <DeepStat label="Cannabinoids" value={doc.chemotype.dominant_cannabinoids}/>
            <DeepStat label="Terpenes" value={doc.chemotype.dominant_terpenes}/>
            <DeepStat label="Aroma / flavor" value={doc.chemotype.aroma_flavor}/>
          </div>
        </section>
      )}

      {/* v3.89i — Surfaces top-level terpenes + Demarily provenance chips. */}
      {(Array.isArray(doc.terpenes) && doc.terpenes.length > 0) ||
        (Array.isArray(doc.demarily_effects) && doc.demarily_effects.length > 0) ||
        (Array.isArray(doc.demarily_flavors) && doc.demarily_flavors.length > 0) ? (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6"
                 data-testid="lineage-deep-terpenes">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="label-eyebrow" style={{ color: "var(--text-muted)" }}>
              Terpene & sensory profile
            </div>
            {doc.demarily_attribution && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] border"
                    style={{ color: "var(--gold)", borderColor: "var(--gold-soft, var(--gold))", borderRadius: 2 }}
                    title="Enriched from the Demarily public strain database"
                    data-testid="lineage-demarily-attribution">
                Data: {doc.demarily_attribution}
              </span>
            )}
          </div>
          {Array.isArray(doc.terpenes) && doc.terpenes.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5" style={{ color: "var(--text-muted)" }}>Terpenes</div>
              <div className="flex flex-wrap gap-1.5" data-testid="lineage-terpene-chips">
                {doc.terpenes.map((t) => (
                  <span key={t}
                        className="px-2 py-0.5 text-[11px] font-mono uppercase tracking-[0.14em] border"
                        style={{ color: "var(--gold)", borderColor: "var(--border)", borderRadius: 2 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(doc.demarily_effects) && doc.demarily_effects.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5" style={{ color: "var(--text-muted)" }}>Reported effects</div>
              <div className="flex flex-wrap gap-1.5">
                {doc.demarily_effects.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)]"
                        style={{ color: "var(--text-2)", borderRadius: 2 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(doc.demarily_flavors) && doc.demarily_flavors.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5" style={{ color: "var(--text-muted)" }}>Reported flavors</div>
              <div className="flex flex-wrap gap-1.5">
                {doc.demarily_flavors.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)]"
                        style={{ color: "var(--text-2)", borderRadius: 2 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* v3.89j — Engagement loop: collectors can flag missing / wrong
              terpene data. Reuses the existing submission modal so admins
              moderate via the standard review queue. */}
          <button
            type="button"
            onClick={() => setCorrectOpen(true)}
            className="mt-4 text-[11px] font-mono uppercase tracking-[0.18em] underline hover:text-[var(--gold)] transition-colors"
            style={{ color: "var(--text-muted)" }}
            data-testid="lineage-suggest-enrichment"
          >
            Spot something missing? Suggest a correction →
          </button>
        </section>
      ) : null}

      {doc.morphology && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-morphology">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Morphology & grow traits</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Leaf type" value={DEEP_LABELS.leaf[doc.morphology.leaf_type]}/>
            <DeepStat label="Photoperiod" value={DEEP_LABELS.photo[doc.morphology.photoperiod_type]}/>
            <DeepStat label="Sex expression" value={DEEP_LABELS.sex[doc.morphology.sex_expression]}/>
            <DeepStat label="Growth" value={doc.morphology.growth_structure}/>
            <DeepStat label="Flowering" value={doc.morphology.flowering_time}/>
            <DeepStat label="Resin" value={DEEP_LABELS.resin[doc.morphology.resin_production]}/>
          </div>
          {(doc.morphology.yield_notes || doc.morphology.resistance_notes) && (
            <div className="mt-3 text-xs space-y-2" style={{color:"var(--text-2)"}}>
              {doc.morphology.yield_notes && <p><b style={{color:"var(--text-muted)"}}>Yield:</b> {doc.morphology.yield_notes}</p>}
              {doc.morphology.resistance_notes && <p><b style={{color:"var(--text-muted)"}}>Resistance:</b> {doc.morphology.resistance_notes}</p>}
            </div>
          )}
        </section>
      )}

      {doc.verification && (doc.verification.lineage_confidence || (doc.verification.evidence_types || []).length > 0 || doc.verification.dispute_status) && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-verification">
          <div className="label-eyebrow mb-3" style={{color:"var(--gold)"}}>Confidence & evidence</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Lineage confidence" value={DEEP_LABELS.conf[doc.verification.lineage_confidence]}/>
            <DeepStat label="Dispute status" value={DEEP_LABELS.disp[doc.verification.dispute_status]}/>
            <DeepStat label="Evidence count" value={(doc.verification.evidence_types || []).length || "—"}/>
          </div>
          {(doc.verification.evidence_types || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {doc.verification.evidence_types.map(e => (
                <span key={e} className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] border border-[var(--border)]" style={{color:"var(--text-2)"}}>
                  {DEEP_LABELS.evid[e] || e}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {doc.genetic_testing && (doc.genetic_testing.test_available || doc.genetic_testing.lab || doc.genetic_testing.marker_type) && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-genetic-testing">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Genetic testing</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Test available" value={doc.genetic_testing.test_available}/>
            <DeepStat label="Lab" value={doc.genetic_testing.lab}/>
            <DeepStat label="Marker type" value={DEEP_LABELS.marker[doc.genetic_testing.marker_type]}/>
            <DeepStat label="Sample ID" value={doc.genetic_testing.sample_id}/>
            <DeepStat label="Cluster" value={doc.genetic_testing.cluster_group}/>
            <DeepStat label="Ancestry" value={doc.genetic_testing.ancestry_composition}/>
          </div>
          {doc.genetic_testing.closest_matches && (
            <p className="text-xs mt-3" style={{color:"var(--text-2)"}}>
              <b style={{color:"var(--text-muted)"}}>Closest matches:</b> {doc.genetic_testing.closest_matches}
            </p>
          )}
        </section>
      )}

      {doc.legal_credit && (doc.legal_credit.original_breeder_credit || doc.legal_credit.preservation_credit || doc.legal_credit.ip_status) && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-legal">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Credit & rights</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <DeepStat label="Original breeder credit" value={doc.legal_credit.original_breeder_credit}/>
            <DeepStat label="Preservation credit" value={doc.legal_credit.preservation_credit}/>
            <DeepStat label="Commercial release" value={doc.legal_credit.commercial_release_date}/>
            <DeepStat label="IP status" value={doc.legal_credit.ip_status}/>
            <DeepStat label="Submitted by" value={doc.legal_credit.submitted_by}/>
            <DeepStat label="Claimed by breeder" value={DEEP_LABELS.yn[doc.legal_credit.claimed_by_breeder]}/>
          </div>
          {doc.legal_credit.license_restrictions && (
            <p className="text-xs mt-3 whitespace-pre-line" style={{color:"var(--text-2)"}}>{doc.legal_credit.license_restrictions}</p>
          )}
          {doc.legal_credit.do_not_rename && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] border" style={{color:"var(--secondary)", borderColor:"var(--secondary)"}}>
              Do not rename / do not claim
            </div>
          )}
        </section>
      )}

      {(doc.evidence_files || []).length > 0 && (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-4 mb-6" data-testid="lineage-deep-evidence-files">
          <div className="label-eyebrow mb-3" style={{color:"var(--text-muted)"}}>Evidence files</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {doc.evidence_files.filter(f => f && (f.public !== false)).map((f, i) => (
              <div key={i} className="border border-[var(--border)] overflow-hidden">
                {f.url ? <img src={f.url} alt="" className="w-full aspect-square object-cover"/> : <div className="aspect-square bg-[var(--surface-2)]"/>}
                <div className="p-2 text-[11px]" style={{color:"var(--text-2)"}}>
                  <div className="font-mono uppercase tracking-[0.16em] text-[10px]" style={{color:"var(--gold)"}}>
                    {DEEP_LABELS.efile[f.category] || f.category || "File"}
                  </div>
                  {f.date_taken && <div className="text-[10px] mt-0.5" style={{color:"var(--text-muted)"}}>{f.date_taken}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}


      {doc.source_notes && (
        <section className="bg-[var(--surface-2)] border border-[var(--border)] p-4 mb-8 text-sm whitespace-pre-line"
                 style={{color:"var(--text-2)"}}>
          <div className="label-eyebrow mb-2" style={{color:"var(--text-muted)"}}>Source notes</div>
          {doc.source_notes}
        </section>
      )}

      {/* v3.99c.73 — Now Trending sliver. Matches the current strain by
          slug → name → breeder → family. Empty-state has a branded
          fallback message pointing at Forge Feed. */}
      <StrainTrendingStrip slug={doc.slug || doc.id} />

        </main>

        {/* v3.97 — Sticky sidebar (right rail). Mirrors design_guidelines
            "detail_page" — breeder credit, quick anchor jumps, primary
            buy CTA. Collapses below the main column on mobile. */}
        <aside className="lg:col-span-4 lg:sticky lg:top-6 lg:self-start space-y-3"
               data-testid="lineage-detail-sidebar">
          {(doc.related_listings || []).length > 0 ? (
            <a href="#available-on-cannaforge"
               className="block w-full text-center px-5 py-4 border-2 hover:brightness-110 transition-all"
               style={{
                 background: "var(--primary-text)",
                 color: "#0B0B0B",
                 borderColor: "var(--primary-text)",
                 borderRadius: 999,
               }}
               data-testid="lineage-sidebar-buy-cta">
              <div className="font-display text-lg leading-none mb-0.5">
                Buy on CannaForge
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">
                {doc.related_listings.length} live listing{doc.related_listings.length === 1 ? "" : "s"} ·
                from ${Math.min(...doc.related_listings.map((l) => l.price || 0)).toFixed(2)}
              </div>
            </a>
          ) : (
            <button onClick={toggleSave} disabled={saveBusy}
                    className="block w-full text-center px-5 py-4 border-2 hover:bg-[var(--gold)] hover:text-[#0B0B0B] transition-colors"
                    style={{
                      background: "transparent",
                      color: "var(--gold)",
                      borderColor: "var(--gold)",
                      borderRadius: 999,
                    }}
                    data-testid="lineage-sidebar-notify-cta">
              <div className="font-display text-base leading-none mb-0.5">
                {doc.saved_by_me ? "You're tracking this strain" : "Notify when listed"}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">
                {doc.saved_by_me ? "Tap to remove" : "Save · get alerts"}
              </div>
            </button>
          )}

          {/* Breeder credit card */}
          {(doc.related_breeders || []).length > 0 && (() => {
            const v = doc.related_breeders[0];
            return (
              <Link to={`/breeder/${v.store_slug || v.id}`}
                    className="block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] p-4 transition-colors"
                    data-testid="lineage-sidebar-breeder-card">
                <div className="label-eyebrow mb-2" style={{color:"var(--text-muted)"}}>Credited breeder</div>
                <div className="flex items-center gap-3">
                  {v.logo_url ? (
                    <img src={v.logo_url} alt="" className="w-12 h-12 object-cover border border-[var(--border)] shrink-0"/>
                  ) : (
                    <div className="w-12 h-12 bg-[var(--surface-2)] border border-[var(--border)] shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-serif text-base truncate" style={{color:"var(--text)"}}>{v.store_name}</div>
                    <div className="text-[11px] font-mono flex items-center gap-1.5 flex-wrap" style={{color:"var(--text-muted)"}}>
                      <span>★ {(v.rating || 0).toFixed(1)} · {v.review_count || 0} reviews</span>
                      <span className="inline-flex items-center gap-0.5"
                            data-testid="lineage-sidebar-breeder-followers">
                        <Users className="w-2.5 h-2.5" />
                        {v.follower_count || 0}
                      </span>
                    </div>
                  </div>
                </div>
                {doc.related_breeders.length > 1 && (
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em] mt-2"
                       style={{color:"var(--text-muted)"}}>
                    +{doc.related_breeders.length - 1} more below
                  </div>
                )}
              </Link>
            );
          })()}

          {/* Quick-jump anchor nav */}
          <nav className="bg-[var(--surface)] border border-[var(--border)] p-3"
               data-testid="lineage-sidebar-nav">
            <div className="label-eyebrow mb-2" style={{color:"var(--text-muted)"}}>On this page</div>
            <ul className="space-y-1.5 text-xs font-mono">
              <SidebarJump href="#parentage" label="Parentage" testid="lineage-sidebar-jump-parentage"/>
              {(doc.related_listings || []).length > 0 && (
                <SidebarJump href="#available-on-cannaforge"
                             label={`Available · ${doc.related_listings.length}`}
                             accent="var(--primary-text)"
                             testid="lineage-sidebar-jump-listings"/>
              )}
              {(doc.children || []).length > 0 && (
                <SidebarJump href="#descendants"
                             label={`Descendants · ${doc.children.length}`}
                             testid="lineage-sidebar-jump-descendants"/>
              )}
              {(doc.related_breeders || []).length > 0 && (
                <SidebarJump href="#related-breeders" label="Related breeders"
                             testid="lineage-sidebar-jump-breeders"/>
              )}
              {(doc.chemotype || doc.morphology) && (
                <SidebarJump href="#cultivar-data" label="Cultivar data"
                             testid="lineage-sidebar-jump-cultivar"/>
              )}
              <li>
                <Link to={`/lineage?tab=map&center=${encodeURIComponent(doc.slug || doc.id)}`}
                      className="inline-flex items-center gap-1.5 hover:text-[var(--gold)] transition-colors uppercase tracking-[0.16em] text-[11px]"
                      style={{color:"var(--gold)"}}
                      data-testid="lineage-sidebar-jump-map">
                  <Network className="w-3 h-3"/> Map this strain
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
      </div>

      <div className="text-[11px] font-mono pt-4 border-t border-[var(--border)]" style={{color:"var(--text-muted)"}}>
        Record created {doc.created_at?.slice(0, 10)} · {doc.created_by_role || "admin"} ·{" "}
        Last updated {doc.updated_at?.slice(0, 10)}. Lineage data is community-curated and may be revised.
      </div>

      {/* v3.99c.62 — Cross-link footer */}
      <section className="mt-10 pt-6 border-t border-[var(--border)]" data-testid="lineage-cross-links">
        <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>Continue exploring</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to={`/lineage?tab=map&center=${encodeURIComponent(doc.slug || doc.id)}`}
                className="block p-4 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                data-testid="lineage-cross-link-map">
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Map this strain</div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>Open in Genetics Map</div>
          </Link>
          {doc.strain_family && (
            <Link to={`/families/${doc.strain_family.toLowerCase()}`}
                  className="block p-4 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                  data-testid="lineage-cross-link-family">
              <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>{doc.strain_family} family</div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>Working lines &amp; descendants</div>
            </Link>
          )}
          <Link to="/breeders"
                className="block p-4 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                data-testid="lineage-cross-link-breeders">
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Verified Breeders</div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>Hand-onboarded directory</div>
          </Link>
          <Link to="/genetics-guide"
                className="block p-4 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                data-testid="lineage-cross-link-guide">
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Genetics Guide</div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>Lineage terms · breeding 101</div>
          </Link>
        </div>
      </section>

      {/* v3.99ad — Grow Logs strip: real cultivation evidence for this
          strain. Renders nothing until at least one public log exists. */}
      <GrowLogStrip strainIdOrSlug={doc.slug || doc.id} title={`Grow logs · ${doc.strain_name}`} />

      {/* v3.99ai — Phase 1 P2: Forge Index for this strain. Renders nothing
          until at least one grow log or approved review exists. */}
      <div className="mt-10">
        <ForgeIndexPanel scope="strain" idOrSlug={doc.slug || doc.id} />
      </div>

      {correctOpen && (
        <LineageSubmitModal targetLineageId={doc.id} targetName={doc.strain_name}
                            onClose={() => setCorrectOpen(false)}
                            onSubmitted={() => { setCorrectOpen(false); toast.success("Correction submitted — pending admin review."); }}/>
      )}
    </div>
  );
}

/**
 * v3.70 — image gallery with featured/thumbs interaction.
 */
function LineageGallery({ images, featuredIndex, strainName }) {
  const [active, setActive] = useState(
    images && images.length > featuredIndex ? featuredIndex : 0
  );
  if (!images || images.length === 0) return null;
  return (
    <section className="mb-6" data-testid="lineage-gallery">
      <div className="label-eyebrow mb-2" style={{color:"var(--text-muted)"}}>
        Gallery · {images.length} image{images.length === 1 ? "" : "s"}
      </div>
      <div className="bg-[var(--surface)] border border-[var(--border)] p-2 md:p-3">
        <div className="w-full bg-black aspect-[16/9] flex items-center justify-center mb-2">
          <img
            src={images[active]}
            alt={`${strainName} — image ${active + 1}`}
            className="max-w-full max-h-full object-contain"
            data-testid="lineage-gallery-active"
          />
        </div>
        {images.length > 1 && (
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
            {images.map((url, i) => (
              <button key={i} onClick={() => setActive(i)}
                      className={`aspect-square border ${i === active ? "border-[var(--gold)]" : "border-[var(--border)] hover:border-[var(--text-muted)]"}`}
                      data-testid={`lineage-gallery-thumb-${i}`}>
                <img src={url} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-3">
      <div className="label-eyebrow mb-1" style={{color:"var(--text-muted)"}}>{label}</div>
      <div className="text-sm font-mono" style={{color:"var(--text)"}}>{value}</div>
    </div>
  );
}

// v3.97 — Magazine-layout sidebar quick-jump link.
function SidebarJump({ href, label, accent, testid }) {
  return (
    <li>
      <a href={href}
         className="inline-flex items-center gap-2 hover:text-[var(--gold)] transition-colors uppercase tracking-[0.16em] text-[11px]"
         style={{ color: accent || "var(--text-2)" }}
         data-testid={testid}>
        <span className="opacity-50">→</span> {label}
      </a>
    </li>
  );
}
